import { DateTime } from "luxon";

import { PatientCaseProviderRoutingStrategy, PatientCaseType } from "@/shared/models/patient-case";
import type { SchedulingPatientCaseConfig } from "@/src/lib/comms/scheduling/conversation-policy";
import { AppointmentType } from "@/src/models/appointment";
import { MessageDirection, MessageIntent, type Message } from "@/src/models/comms/message";
import type { AppointmentSlot } from "@/src/models/comms/scheduling-schemas";
import {
  Classification,
  DEFAULT_CLASSIFICATIONS_TO_DISPLAY,
  type ClassificationSection,
} from "@/src/models/constants/classifications";
import { Workflow, type WorkflowType } from "@/src/models/constants/group";
import { getCoverageDisplayName } from "@/src/models/coverage";
import { type DiagnosisItem, type Referral } from "@/src/models/documents/referral";
import { getPatientPrimaryCoverage, type Patient } from "@/src/models/patient";
import { getPersonName } from "@/src/models/person";

// The Camelback clinic's physical address (2777 East Camelback Road, Suite 200, Phoenix, AZ 85016;
// also in faq.md). Used as the fallback identity check when an extracted receiving location did not
// resolve to the location roster — the canonical check is the roster-matched ehrId.
export const TPR_BILTMORE_CLINIC_ADDRESS_LINE_1 = "2777 East Camelback Road";
export const TPR_BILTMORE_CLINIC_POSTAL_CODE = "85016";
// Fetch/auth window. Receiving-provider selection uses its own separate 60-day window
// (TPR_PROVIDER_SELECTION_LOOKAHEAD_DAYS) — same value, different purpose, keep decoupled.
export const UPCOMING_APPOINTMENT_LOOKAHEAD_DAYS = 60;

/** Defines idempotent local, Athena Patient Notes, and referral action-note updates for TPR SMS outreach. */
export function getTprSmsOutreachPatientNoteUpdate(
  existingPatientNotes: Patient["notes"],
  timezone: string,
  messages: Message[],
  optedOut = false
): { localNotes: string; ehrNotes: string[] } {
  let localNotes = existingPatientNotes?.trim() ?? "";
  const ehrNotes: string[] = [];
  const outreachNotes = messages
    .filter((message) => message.direction === MessageDirection.OUTBOUND)
    .map((message) => {
      const date = DateTime.fromISO(message.createdAt).setZone(timezone).toFormat("MM/dd/yyyy");

      switch (message.messageIntent) {
        case MessageIntent.INITIAL_OUTREACH:
          return `Valerie Health - Sent initial outreach message on ${date}`;
        case MessageIntent.NO_RESPONSE_FOLLOW_UP:
          return `Valerie Health - No response, sent follow up message on ${date}`;
        case MessageIntent.NO_RESPONSE_CLOSING:
          return "Valerie Health - No response at 48 hrs, please call patient to schedule";
        case MessageIntent.NO_RESPONSE_HAS_APPOINTMENT:
          return null;
        default:
          return null;
      }
    });

  if (optedOut) {
    outreachNotes.push("Valerie Health - Patient opted out of SMS Scheduling. Please call to schedule");
  }

  for (const note of outreachNotes) {
    if (note && !localNotes.includes(note)) {
      ehrNotes.push(note);
      localNotes = [localNotes, note].filter(Boolean).join("\n");
    }
  }

  return { localNotes, ehrNotes };
}

export type TprPrescriptionRefillPatientCaseConfig = {
  departmentId: string;
  assignedTo: string | null;
  documentSource: string;
  documentSubclass: string;
  priority: "1" | "2";
  outboundOnly: boolean;
  subject: string;
};

const TPR_PRESCRIPTION_REFILL_PATIENT_CASE_CONFIG: TprPrescriptionRefillPatientCaseConfig = {
  departmentId: "648",
  assignedTo: null,
  documentSource: "PATIENT",
  documentSubclass: "REFILL",
  priority: "2",
  outboundOnly: false,
  subject: "Prescription Refill",
};

export function getTprPatientCaseConfig(caseType: PatientCaseType): SchedulingPatientCaseConfig | null {
  switch (caseType) {
    case PatientCaseType.PRESCRIPTION_REFILL:
      return {
        ...TPR_PRESCRIPTION_REFILL_PATIENT_CASE_CONFIG,
        providerRouting: {
          strategy: PatientCaseProviderRoutingStrategy.PREVIOUS_CASE_THEN_PRIMARY,
          previousCaseSearchTerm: "refill",
        },
      };
    default:
      return null;
  }
}

const BOOKING_NOTE_SEPARATOR = " | ";
const NOT_PROVIDED = "Not provided";

type TprBookingNoteAppointmentSlot = Pick<AppointmentSlot, "appointmentType" | "start" | "end">;

export enum TprBiltmoreCamelbackProviderNpi {
  DIANA_THAYER = "1063975076",
  DANIELLE_STURM = "1164709788",
  LORRAINE_HOMES = "1255375481",
  CHRIS_LICHTENWALTER = "1275582132",
  MARIUM_MUZAFFAR = "1306191242",
  PETER_MAKI = "1306886775",
  TIMOTHY_BYRNE = "1376585232",
  NICKOLAUS_GRAMZE = "1487972964",
  AKRAM_IBRAHIM = "1508029802",
  LAWRENCE_KLINE = "1568463669",
  NEAL_KLEIN = "1639111743",
  KRISTEN_JENSEN = "1770834798",
  JODY_ECKHARDT = "1871650564",
  KEVIN_MURPHY = "1881777613",
  ELIZABETH_STEFFENS = "1952123077",
  RENZO_CATALDO = "1174526677",
  AKIL_LOLI = "1962453803",
  MARWAN_BAHU = "1114920618",
}

export function isTprBiltmoreTelehealthProviderNpi(providerNpi: string | null | undefined): boolean {
  return providerNpi === TprBiltmoreCamelbackProviderNpi.CHRIS_LICHTENWALTER;
}

export enum TprBiltmoreAhiProviderNpi {
  MICHELLE_MIX = "1831385533",
  AYMAN_JAMAL = "1083600845",
}

export enum TprBiltmoreCasaGrandeProviderNpi {
  JASON_COOL = "1700886512",
}

export enum TprBiltmoreWestProviderNpi {
  JAMES_KIM = "1730262882",
  RICHARD_PHAM = "1124439377",
}

export enum TprBiltmoreGoodyearProviderNpi {
  SHARON_SADEGHINIA = "1770608275",
}

export enum TprBiltmoreArrowheadProviderNpi {
  RAHOOL_KARNIK = "1932183563",
  KRISTINA_SKINNER = "1790191039",
  DAVID_PRUTZMAN = "1477993152",
  MAEDEH_KHOLGHI = "1376049528",
}

export enum TprBiltmoreScottsdaleProviderNpi {
  ASHISH_GUPTA = "1982782470",
  WILLIAM_JAFFE = "1083648158",
}

export enum TprBiltmorePeoriaProviderNpi {
  SAIFULDEEN_AL_QAISI = "1053673723",
}

export type TprBiltmoreNetworkProviderNpi =
  | TprBiltmoreCamelbackProviderNpi
  | TprBiltmoreAhiProviderNpi
  | TprBiltmoreCasaGrandeProviderNpi
  | TprBiltmoreWestProviderNpi
  | TprBiltmoreGoodyearProviderNpi
  | TprBiltmoreArrowheadProviderNpi
  | TprBiltmoreScottsdaleProviderNpi
  | TprBiltmorePeoriaProviderNpi;

export const TPR_SUPERVISING_PHYSICIAN_NPI_BY_APP_NPI: Readonly<Record<string, string>> = {
  [TprBiltmoreCamelbackProviderNpi.DIANA_THAYER]: TprBiltmoreCamelbackProviderNpi.AKRAM_IBRAHIM,
  [TprBiltmoreCamelbackProviderNpi.ELIZABETH_STEFFENS]: TprBiltmoreCamelbackProviderNpi.AKRAM_IBRAHIM,
  [TprBiltmoreCamelbackProviderNpi.DANIELLE_STURM]: TprBiltmoreCamelbackProviderNpi.TIMOTHY_BYRNE,
  [TprBiltmoreCamelbackProviderNpi.KRISTEN_JENSEN]: TprBiltmoreCamelbackProviderNpi.TIMOTHY_BYRNE,
  [TprBiltmoreCamelbackProviderNpi.KEVIN_MURPHY]: TprBiltmoreCamelbackProviderNpi.PETER_MAKI,
  "1760055099": TprBiltmoreCamelbackProviderNpi.MARIUM_MUZAFFAR, // Rico Segun
  "1699852053": TprBiltmoreCamelbackProviderNpi.MARWAN_BAHU, // Amilee Ning
  "1932399870": TprBiltmoreCamelbackProviderNpi.AKIL_LOLI, // Kristin V. Franco
};

export function getTprSupervisingPhysicianNpi(providerNpi: string): string | null {
  return TPR_SUPERVISING_PHYSICIAN_NPI_BY_APP_NPI[providerNpi] ?? null;
}

/**
 * Returns the bounded physician/APP family for a TPR referral's receiving provider.
 *
 * Keeping this lookup bidirectional resolves the same family whether the referral names the
 * physician or one of their APPs. Providers without an audited family relationship remain isolated
 * to themselves.
 */
export function getTprProviderFamilyNpis(providerNpi: string): readonly string[] {
  const supervisingPhysicianNpi = getTprSupervisingPhysicianNpi(providerNpi);
  const physicianNpi = supervisingPhysicianNpi ?? providerNpi;
  const associatedAppNpis = Object.entries(TPR_SUPERVISING_PHYSICIAN_NPI_BY_APP_NPI).flatMap(
    ([appNpi, supervisorNpi]) => (supervisorNpi === physicianNpi ? [appNpi] : [])
  );

  return [physicianNpi, ...associatedAppNpis];
}

// General cardiac imaging (IMAGING_RESULTS_CARDIAC) is the fallback when a study is clearly cardiac
// but doesn't clearly match one of these more specific subtypes — see classification-prompts.ts.
const TPR_CARDIAC_IMAGING_CLASSIFICATIONS: Classification[] = [
  Classification.IMAGING_RESULTS_CARDIAC,
  Classification.IMAGING_RESULTS_CARDIAC_LEG_IMAGING,
  Classification.IMAGING_RESULTS_ECHO,
  Classification.IMAGING_RESULTS_EKG,
  Classification.IMAGING_RESULTS_HOLTER_MONITOR,
  Classification.IMAGING_RESULTS_STRESS_TEST,
  Classification.IMAGING_RESULTS_NM,
  Classification.IMAGING_RESULTS_CT,
  Classification.IMAGING_RESULTS_MRI,
  Classification.IMAGING_RESULTS_PET,
  Classification.IMAGING_RESULTS_ULTRASOUND,
];

export function getTprClassificationsToExtract(): Classification[] {
  return [
    ...DEFAULT_CLASSIFICATIONS_TO_DISPLAY,
    Classification.REFERRAL_AUTH,
    Classification.PHARMACY_RELATED_MEDICATION_LIST,
    ...TPR_CARDIAC_IMAGING_CLASSIFICATIONS,
  ];
}

export function getTprClassificationsToDisplayForSelection(): ClassificationSection[] {
  return [{ label: "Classification", options: getTprClassificationsToExtract() }];
}

export function getTprWorkflows(): Partial<Record<WorkflowType, Classification[]>> {
  return {
    [Workflow.REFERRAL]: [Classification.REFERRAL, Classification.REFERRAL_AUTH],
  };
}

export function getTprScheduleConfigPath(): string {
  return "configuration/groups/tpr/rules/schedule.yaml";
}

function getDiagnosisOrComplaint(referral: Referral | null, additionalDetails?: string): string {
  const reasonForReferral = referral?.reasonForReferral?.trim();

  if (reasonForReferral) return reasonForReferral;

  const diagnosisList: DiagnosisItem[] = referral?.diagnosisList ?? [];
  const diagnoses = diagnosisList
    .map(({ diagnosisCode, diagnosisText }) => diagnosisText?.trim() || diagnosisCode?.trim())
    .filter((diagnosis): diagnosis is string => diagnosis !== undefined && diagnosis !== "");

  return diagnoses.join(", ") || additionalDetails?.trim() || NOT_PROVIDED;
}

function getAppointmentLabel(appointmentSlot: TprBookingNoteAppointmentSlot): string {
  switch (appointmentSlot.appointmentType) {
    case AppointmentType.INITIAL_CONSULTATION:
    case AppointmentType.NEW_PATIENT_CHECKIN:
      return "New Pat";
    case AppointmentType.FOLLOW_UP:
      return `FU ${getSlotDurationMinutes(appointmentSlot)}`;
    default:
      return `EST ${getSlotDurationMinutes(appointmentSlot)}`;
  }
}

function getSlotDurationMinutes(appointmentSlot: TprBookingNoteAppointmentSlot): number {
  const start = DateTime.fromISO(appointmentSlot.start, { setZone: true });
  const end = DateTime.fromISO(appointmentSlot.end, { setZone: true });

  return Math.max(1, Math.round(end.diff(start, "minutes").minutes));
}

export function buildTprBookingNote({
  appointmentSlot,
  patient,
  referral,
  additionalDetails,
}: {
  appointmentSlot: TprBookingNoteAppointmentSlot;
  patient: Patient;
  referral: Referral | null;
  additionalDetails?: string;
}): string {
  const diagnosisOrComplaint = getDiagnosisOrComplaint(referral, additionalDetails);
  const coverage = getPatientPrimaryCoverage(patient) ?? referral?.primaryCoverage;
  const insurance = getCoverageDisplayName({
    coverage,
    includesPlan: true,
    includeSubscriberName: false,
  });
  const parts = [getAppointmentLabel(appointmentSlot), diagnosisOrComplaint, insurance];

  if (
    appointmentSlot.appointmentType === AppointmentType.INITIAL_CONSULTATION ||
    appointmentSlot.appointmentType === AppointmentType.NEW_PATIENT_CHECKIN
  ) {
    const referringProviderName = getPersonName(referral?.referringProvider).trim();

    if (referringProviderName) {
      parts.push(`Referred by ${referringProviderName}`);
    }
  }

  const details = additionalDetails?.trim();

  if (details && details !== diagnosisOrComplaint) {
    parts.push(details);
  }

  return parts.join(BOOKING_NOTE_SEPARATOR);
}
